// Phase B 다국어 번역 — vite plugin `/api/tpt/translate` 호출.
// 결과는 메모리 캐시 (messageId+targetLang 기준).
// ⚠️ `../i18n` 을 정적 import 하면 module 평가 순서가 꼬여 render 가 깨지므로(아래 setPrefLang 주석 참조)
//    이미 초기화된 i18next 싱글톤을 직접 import 한다 (호출 시점에 .t 만 사용).
import i18next from 'i18next';
import { findMentionNames } from '../utils/mentionNames';
import { useAuthStore } from '../stores/authStore';

// 🛡 번역 라우트 인증 헤더 (2026-08-20 외부 노출 재점검) — 서버가 종전 무인증이라 외부에서 번역 엔진
//   비용을 태울 수 있었다. 로그인돼 있으면 항상 싣는다(서버는 헤더가 오면 검증, 추후 require 로 잠금).
function txAuthHeaders(): Record<string, string> {
	try {
		const { authToken, userId } = useAuthStore.getState();
		if (authToken && userId) return { 'X-Auth-Token': authToken, 'X-User-Id': userId };
	} catch { /* 스토어 미초기화 — 헤더 없이 (서버 log 모드가 허용) */ }
	return {};
}

export type LangCode = 'ko' | 'en' | 'vi' | 'es' | 'zh';

export const LANG_LABEL: Record<LangCode, string> = {
	ko: '한국어', en: 'English', vi: 'Tiếng Việt', es: 'Español', zh: '中文',
};

// 언어명을 현재 UI 언어(=선호언어)로 표기. 선호언어가 한국어면 "영어·베트남어·중국어…", 영어면 "English·Vietnamese…".
//   i18n `lang.<code>` 키 사용(없으면 원어명 LANG_LABEL 폴백). 자동번역/번역 메뉴 라벨용.
export function langLabel(code: LangCode): string {
	try { return i18next.t(`lang.${code}`, { defaultValue: LANG_LABEL[code] }); } catch { return LANG_LABEL[code]; }
}

const cache = new Map<string, string>();

// 🌐 '내 언어'(수신 번역 언어) — UI 언어(localStorage 'tpt-lang', i18n.ts)와 **별개 키**('tpt-tx-lang').
//   예전엔 'tpt-lang' 을 공유해서, 채팅 하단/헤더에서 번역 언어만 골라도 다음 새로고침·재로그인 때
//   UI 언어까지 바뀌었다 (2026-08-12 "새로고침마다 언어가 자동 변경" — 키 분리로 해소).
//   미설정(레거시 사용자)이면 'tpt-lang'(=UI 언어) 폴백 → 기존 동작과 동일하게 시작.
const VALID_LANGS = ['ko', 'en', 'vi', 'es', 'zh'];

export function getPrefLang(): LangCode {
	try {
		const v = localStorage.getItem('tpt-tx-lang') as LangCode | null;
		if (v && VALID_LANGS.includes(v)) return v;
		const legacy = localStorage.getItem('tpt-lang') as LangCode | null;
		if (legacy && VALID_LANGS.includes(legacy)) return legacy;
	} catch { /* private mode */ }
	// 저장된 선호언어가 없으면 'ko' — i18n.ts readInitialLang() 과 동일한 기본값.
	//   ⚠️ 예전엔 navigator.language 로 자동감지했으나, i18n 은 'ko' 기본이라 영어폰에서
	//   "설정=영어인데 화면=한글" 불일치가 났다(2026-07-06 수정). 실제 언어는 설정에서 선택.
	return 'ko';
}
export function pushPrefLang(lang: LangCode): void {
	const h = txAuthHeaders();
	if (!h['X-Auth-Token']) return;   // 스토어 미초기화 — 다음 설정 변경/부트스트랩 때 다시 맞춘다
	void fetch('/api/tpt/user-tx-lang', {
		method: 'POST', headers: { 'Content-Type': 'application/json', ...h }, body: JSON.stringify({ lang }),
	}).catch(() => undefined);
}

export function setPrefLang(lang: LangCode, opts: { push?: boolean; at?: number } = {}): void {
	try {
		localStorage.setItem('tpt-tx-lang', lang);
		localStorage.setItem('tpt-tx-lang-at', String(opts.at ?? Date.now()));
	} catch { /* private mode */ }
	// 언어가 바뀌면 발신자 선번역 스킵 캐시도 비운다 — 방금 바뀐 언어가 대상 0 판정에 묻히지 않게
	_prefetchSkipUntil.clear();
	if (opts.push !== false) pushPrefLang(lang);
	// ⚠️ 'tpt-lang'(UI 언어)에는 절대 쓰지 않는다 — UI 언어 변경은 SettingsModal 의 i18n.changeLanguage 가
	//   담당(i18n.ts 의 languageChanged 리스너가 'tpt-lang' 저장). translate.ts 가 i18n.ts 를 정적 import
	//   하면 module 평가 순서가 꼬여 render 가 깨지는 케이스가 있어 여기서 i18n 을 직접 만지지 않는다.
	window.dispatchEvent(new CustomEvent('tpt-lang-change', { detail: { lang } }));
}

// 채팅창 별 자동번역 — 기본 켜짐 (2026-08-13 사용자: "기본은 켜기, 방별로만 끄기").
//   방별 키가 '0' 인 방만 꺼진다. 구 전역 키('tpt-auto-tx')는 폐기 — 켜짐이 기본이라 의미가 없다.
export function isAutoTranslateForRoom(roomId: string): boolean {
	try {
		const v = localStorage.getItem(`tpt-auto-tx:${roomId}`);
		// 기록 없는 방 = 이 기기 기본값(일괄 설정, 없으면 켜짐) — 2026-10-02
		if (v === null) return localStorage.getItem(AUTO_TX_DEFAULT_KEY) !== '0';
		return v !== '0';
	} catch { return true; }
}

// 🌐 자동번역 일괄 켜기/끄기 (2026-10-02 형 "설정 > 선호언어에서 세팅하면 모든 방 동일하게") —
//   서버(내 모든 구독 방 + 기본값 '*')를 먼저 바꾸고, 이 기기의 방별 기록·기본값을 같은 값으로 맞춘다.
//   다른 기기는 방에 들어갈 때 room-tx/langs 의 myOn 으로 따라온다. 이후 방에서 개별로 켜고 끄는 건 그대로 된다.
const AUTO_TX_DEFAULT_KEY = 'tpt-auto-tx-default';
export async function setAutoTranslateAllRooms(on: boolean, token: string, userId: string): Promise<{ ok: boolean; rooms: number }> {
	const res = await fetch('/api/tpt/room-tx/pref-all', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'X-User-Id': userId },
		body: JSON.stringify({ on, lang: getPrefLang() }),
	});
	const j = await res.json().catch(() => ({})) as { ok?: boolean; rooms?: number; error?: string };
	if (!res.ok || !j.ok) throw new Error(j.error || `HTTP ${res.status}`);
	try {
		localStorage.setItem(AUTO_TX_DEFAULT_KEY, on ? '1' : '0');
		const keys: string[] = [];
		for (let i = 0; i < localStorage.length; i++) { const k = localStorage.key(i); if (k && k.startsWith('tpt-auto-tx:')) keys.push(k); }
		for (const k of keys) localStorage.setItem(k, on ? '1' : '0');
		// source 'sync' — 열린 방 화면은 토글 표시만 갱신하고 서버로 다시 올리지 않는다(이미 서버가 바뀜)
		window.dispatchEvent(new CustomEvent('tpt-auto-tx-change', { detail: { roomId: '*', on, source: 'sync' } }));
	} catch { /* private mode */ }
	return { ok: true, rooms: j.rooms || 0 };
}

// 이 기기에 '명시 저장된' 방별 토글 — 기록 없으면 null (2026-08-26 동기화 점검).
//   isAutoTranslateForRoom 은 기록 없음을 기본값 true 로 뭉개므로, "서버엔 없고 이 기기에만 남은 설정"을
//   가려내 서버로 올리는 마이그레이션(useAutoTranslate)에는 이 원본 판정이 필요하다.
export function readAutoTranslateRaw(roomId: string): boolean | null {
	try {
		const v = localStorage.getItem(`tpt-auto-tx:${roomId}`);
		return v === null ? null : v !== '0';
	} catch { return null; }
}

// source: 'user'=사용자 토글(서버에도 반영 — useAutoTranslate 의 리스너가 push) / 'sync'=서버값 수신 반영(재push 금지 — 루프 방지)
export function setAutoTranslateForRoom(roomId: string, on: boolean | null, source: 'user' | 'sync' = 'user'): void {
	try {
		const key = `tpt-auto-tx:${roomId}`;
		if (on === null) localStorage.removeItem(key);
		else localStorage.setItem(key, on ? '1' : '0');
		window.dispatchEvent(new CustomEvent('tpt-auto-tx-change', { detail: { roomId, on, source } }));
	} catch { /* private mode */ }
}

// 👁 원본보기 — 번역문 아래 **원문도 같이** 보일지 (2026-09-14 사용자 "자동번역 우측 옆에 자동번역 on/off 식으로 원본보기").
//   자동번역(방별)과 **별개 축**인 개인 설정이라 전역 키 하나. 기본 켜짐(종전 화면 = 번역문+원문 병기).
//   끄면 번역된 메시지는 번역문만 남는다 — 번역이 없는 메시지는 영향 없음(원문이 곧 본문).
const SHOW_ORIGIN_KEY = 'tpt-tx-show-origin';
export function getShowOrigin(): boolean {
	try { return localStorage.getItem(SHOW_ORIGIN_KEY) !== '0'; } catch { return true; }
}
export function setShowOrigin(on: boolean): void {
	try {
		localStorage.setItem(SHOW_ORIGIN_KEY, on ? '1' : '0');
		window.dispatchEvent(new CustomEvent('tpt-tx-show-origin-change', { detail: { on } }));
	} catch { /* private mode */ }
}

export interface TranslateResult {
	translation: string;
	cached: boolean;
	provider: string;
}

// 원문 해시 (djb2→base36) — 서버 저장 번역이 '편집 전 원문' 것인지 판별용.
//   메시지가 수정되면 해시가 달라져 낡은 번역을 표시하지 않는다.
export function srcHashOf(text: string): string {
	let h = 5381;
	for (let i = 0; i < text.length; i++) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
	return (h >>> 0).toString(36);
}

// 💾 서버 저장 번역 배치 조회 (2026-08-11 — "나중에 봐도 알 수 있게") — 한 번 번역된 메시지는
//   tpt_msg_translations 에 남아 재접속·다른 기기(앱 포함)에서도 다시 보인다.
//   🔧 실패(401·503·네트워크)는 null 로 구분해 돌려준다 (2026-08-21 점검) — 종전엔 빈 맵을 돌려줘
//   호출부(시드)가 "저장본 없음"으로 오인하고 조회완료 마킹을 남겨, 그 세션 내내 번역이 안 보였다.
export async function fetchStoredTranslations(ids: string[], lang: LangCode): Promise<Record<string, { text: string; srcHash?: string }> | null> {
	if (ids.length === 0) return {};
	try {
		const res = await fetch('/api/tpt/translations', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', ...txAuthHeaders() },
			body: JSON.stringify({ ids, lang }),
		});
		if (!res.ok) return null;
		const data = await res.json().catch(() => ({}));
		return (data.items || {}) as Record<string, { text: string; srcHash?: string }>;
	} catch { return null; }
}

// 🌐 방별 자동번역 언어 등록 (2026-08-12 사용자) — 발신 즉시 선번역을 하려면 서버가 "이 방에서
//   자동번역을 켠 사람들의 언어"를 알아야 한다. 방 진입/선호언어 변경 시 갱신(30일 유효).
//   🔧 on 은 사용자가 실제 토글했을 때만 보낸다 (2026-08-21 — "껐는데 다시 켜있다") — 종전엔 방 진입만으로
//   그 기기의 로컬 토글 값을 서버에 밀어 올려, 다른 기기에서 끈 설정이 진입 한 번에 ON 으로 뒤집혔다.
//   on 생략 시 서버는 기존 on 값을 보존하고 lang·갱신시각만 upsert 한다.
export async function registerRoomTxPref(rid: string, lang: LangCode, on: boolean | undefined, token: string, userId: string): Promise<void> {
	if (!rid || !token || !userId) return;
	try {
		await fetch('/api/tpt/room-tx/pref', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'X-User-Id': userId },
			body: JSON.stringify({ rid, lang, ...(typeof on === 'boolean' ? { on } : {}) }),
		});
	} catch { /* 등록 실패는 무해 — 수신자 측 번역 경로가 폴백 */ }
}

// 🌐 방 구성원 선호언어 맵 (2026-08-13 사용자 — "번역 필요는 발신자의 선호언어로 판단") —
//   tpt_room_tx_prefs 에 등록된 userId→lang. 발신자 선호언어가 내 선호언어와 같으면 자동번역하지 않는다.
//   실패 시 빈 맵 — 수신측은 기존 언어 감지 판정으로 폴백(치명 아님).
//   myOn (2026-08-21): 서버에 저장된 '내' 방별 자동번역 토글 — 기기 간 동기화의 정본. 기록 없으면 null.
export async function fetchRoomTxLangs(rid: string, token: string, userId: string): Promise<{ langs: Record<string, string>; myOn: boolean | null }> {
	if (!rid || !token || !userId) return { langs: {}, myOn: null };
	try {
		const res = await fetch(`/api/tpt/room-tx/langs?rid=${encodeURIComponent(rid)}`, {
			headers: { 'X-Auth-Token': token, 'X-User-Id': userId },
		});
		if (!res.ok) return { langs: {}, myOn: null };
		const data = await res.json().catch(() => ({}));
		return {
			langs: (data.langs || {}) as Record<string, string>,
			myOn: typeof data.myOn === 'boolean' ? data.myOn : null,
		};
	} catch { return { langs: {}, myOn: null }; }
}

// 🚀 선번역 — 전송 직후 fire-and-forget. 서버가 그 방의 자동번역 언어들로 미리 번역해 저장하므로
//   수신자는 메시지 도착과 거의 동시에 번역본을 받는다. 대상 언어가 없는 방은 잠시(60초) 호출을 건너뛴다.
//   ⏱ 종전 5분 → 60초 (2026-08-27) — 상대가 방금 선호언어를 바꿨는데 발신자 쪽 캐시 때문에 최대 5분간 선번역이
//   안 나가던 것("선호언어 바꿔도 수 분 뒤 반응")의 발신자 측 원인. 대상 0 방의 왕복은 분당 1회면 충분하다.
const PREFETCH_SKIP_MS = 60_000;
const _prefetchSkipUntil = new Map<string, number>();
// 🔒 방별 멘션 이름표(표시명+별칭) 레지스트리 (2026-09-04) — MessageView 가 방 진입·멤버 갱신마다 등록. 프리페치 호출부 4곳이
//   멤버 맵을 갖고 있지 않아, 여기서 protect(멘션 이름 목록)를 산출한다. 없으면 서버가 `@` 뒤 한 토큰만 보호.
const _roomMentionMaps = new Map<string, Record<string, string>>();
export function setRoomMentionMap(rid: string, map: Record<string, string>): void {
	if (!rid) return;
	_roomMentionMaps.set(rid, map);
	if (_roomMentionMaps.size > 50) { const k = _roomMentionMaps.keys().next().value; if (k !== undefined) _roomMentionMaps.delete(k); }
}
export function prefetchRoomTranslations(rid: string, msgId: string, text: string, srcLang: LangCode, token: string, userId: string, protect?: string[]): void {
	if (!rid || !msgId || !text.trim() || !token || !userId) return;
	if (Date.now() < (_prefetchSkipUntil.get(rid) || 0)) return;
	if (!protect) protect = findMentionNames(text, _roomMentionMaps.get(rid));
	void fetch('/api/tpt/translate/prefetch', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'X-User-Id': userId },
		body: JSON.stringify({ rid, msgId, text, srcLang, srcHash: srcHashOf(text), ...(protect && protect.length ? { protect } : {}) }),
	})
		.then((r) => (r.ok ? r.json() : null))
		.then((j: { queued?: string[] } | null) => {
			// 대상 언어 0 → 이 방엔 자동번역 사용자가 없다. 매 전송마다 왕복하지 않도록 5분 캐시.
			if (j && Array.isArray(j.queued) && j.queued.length === 0) _prefetchSkipUntil.set(rid, Date.now() + PREFETCH_SKIP_MS);
			else _prefetchSkipUntil.delete(rid);
		})
		.catch(() => undefined);
}

// 🔤 언어 감지 전처리 — 멘션(@홍길동)·URL·코드·이모지 숏코드 제거.
//   🔴 멘션 제거가 핵심 (2026-08-12 사용자): 베트남어 메시지 맨 앞에 한국인 멘션(@이재설)이 붙으면
//   "한글이 하나라도 있으면 ko" 였던 예전 구현이 원문 언어를 ko 로 오판해, 한국어 사용자에게는
//   "이미 내 언어" 로 판단되어 번역이 통째로 스킵됐다. isTranslatable(MessageView) 과 같은 규칙.
export function stripNonLinguistic(text: string): string {
	return (text || '')
		.replace(/```[\s\S]*?```/g, ' ')       // 코드 블록
		.replace(/`[^`\n]*`/g, ' ')            // 인라인 코드
		.replace(/https?:\/\/\S+/g, ' ')       // URL
		.replace(/@[\p{L}\p{N}._-]+/gu, ' ')   // 멘션 — 실명(한글·베트남어 등 비ASCII 포함) 전체 제거
		.replace(/:[a-z0-9_+-]+:/gi, ' ');     // :shortcode:
}

// 간단 언어 감지 — 스크립트별 글자 수 비교(다수결). 한 글자만 섞여도 그 언어로 단정하던 방식은
//   혼합 메시지(멘션·인용·고유명사)에서 오판이 잦아 카운트 방식으로 바꿨다.
//   vi/es 가 공유하는 것은 acute(á é í ó ú)뿐 — grave(à è ò)·tilde(ã õ)·circumflex(â ê ô)·
//   U+1EA0~U+1EF9 성조 블록·ăđơư 는 베트남어 신호, ñ ¿ ¡ ü 는 스페인어 신호로 본다.
//   공유 acute 는 vi 신호가 함께 있으면 vi 로, 없으면 es 로 귀속.
/**
 * 🇪🇸 스페인어 힌트 — 성조(á é í ó ú)는 베트남어와 공유하므로, 그것만으로 es 를 단정하면 안 된다.
 *   베트남어와 겹치는 흔한 단어(la·de·en·no·co·que 류 중 vi 에도 있는 것)는 넣지 않고,
 *   스페인어에만 있는 기능어만 둔다. 강세 있는 단어(más·está)는 \b 경계가 깨져 매칭이 불안정해 제외.
 */
const ES_HINT_RE = /\b(los|las|una|por|para|pero|como|cuando|donde|porque|muy|todo|gracias|hola|siempre|ahora)\b/i;
export function detectLang(text: string): LangCode {
	const s = stripNonLinguistic(text);
	const cnt = (re: RegExp) => (s.match(re) || []).length;
	const ko = cnt(/[가-힯]/g);
	const zh = cnt(/[㐀-䶿一-鿿]/g);
	const vi = cnt(/[Ạ-ỹĂăĐđƠơƯưÂÊÔâêôÀÈÌÒÙàèìòùÃÕãõ]/g);
	const esOnly = cnt(/[ñÑ¿¡üÜ]/g);
	const accent = cnt(/[áéíóúÁÉÍÓÚ]/g);
	// 🇻🇳/🇪🇸 성조(á é í ó ú)는 두 언어가 공유한다 (2026-08-18 사용자 "베트남어 오탐 조치").
	//   베트남어 전용 문자(ă đ ơ ư …)가 없는 짧은 글을 es 로 단정하면, 베트남 직원의 글이 es 로 잡혀
	//   vi→vi(번역 불필요)여야 할 것이 번역을 타고, 엔진이 원문을 돌려주면 그게 번역본으로 굳는다
	//   (실측 오판: "sao cái" · "Láo vl" 이 es 로 잡혀 저장본이 오염됐다).
	//   → 스페인어 전용 문자나 스페인어 기능어가 있을 때만 es 로 보고, 그 밖의 성조는 vi 로 귀속한다.
	const esLike = esOnly > 0 || ES_HINT_RE.test(s);
	const viScore = vi > 0 ? vi + accent : (!esLike ? accent : 0);
	const esScore = esLike ? esOnly + accent : 0;
	const max = Math.max(ko, zh, viScore, esScore);
	if (max === 0) return 'en';
	// 🔤 영어 본문 + 한글/한자 몇 글자 (2026-10-23 형 "Dear 팀장님, I didn't measured…" 가 ko 로 잡혀 한국 사람 쪽 번역이 건너뛰어짐) —
	//   라틴 글자가 한글의 4배·한자의 5배를 넘고 vi/es 신호가 없으면 영어. 서버 txNormalizeSource 와 같은 규칙(바꾸면 함께).
	if (viScore === 0 && esScore === 0 && cnt(/[A-Za-z]/g) > ko * 4 + zh * 5) return 'en';
	if (ko === max) return 'ko';
	if (zh === max) return 'zh';
	if (viScore === max) return 'vi';
	return 'es';
}

// Gemini 무료 한도(20/min) 초과 시 클라 circuit-breaker — 일정 시간 호출 자체를 차단.
// 메모리 [Gemini quota breaker] 정책 (영구수정 #23) 의 클라이언트판.
let quotaBlockedUntil = 0;

export class QuotaError extends Error {
	retryAfter: number;
	constructor(retryAfter: number) {
		super(i18next.t('errors.translate.quota', { sec: retryAfter }));
		this.name = 'QuotaError';
		this.retryAfter = retryAfter;
	}
}

export function isTranslateQuotaBlocked(): number {
	const remain = quotaBlockedUntil - Date.now();
	return remain > 0 ? Math.ceil(remain / 1000) : 0;
}

// 🖼 그림 속 글자 번역 (2026-09-10 원격 "대화방 캡처사진도 OCR 해서 번역") — ⋯ 메뉴 [그림 번역] 을 눌렀을 때만.
//   서버(/api/tpt/translate/image)가 요청자 토큰으로 그 첨부를 받아 OCR(Gemini 이미지) → 번역(기존 용어집 API) → 파일별 저장.
//   같은 그림은 누가 눌러도 재호출 없이 저장본. 결과 표시 id 는 `img:<msgId>` (translationsStore, 본문 번역 `<msgId>` 와 별개).
export const imgTxId = (msgId: string): string => `img:${msgId}`;
/** 🗺 줄 좌표(이미지 기준 0~1: x,y 좌상단·w,h 크기) + 원문 s·번역 t·굵게 b — 사진 위 겹쳐 보기 (2026-09-11) */
export interface ImgTxLine { s: string; t: string; x: number; y: number; w: number; h: number; b?: boolean }
export interface ImageTranslateResult { ocr: string; translation: string; lines?: ImgTxLine[]; noText?: boolean; same?: boolean; provider?: string; cached?: boolean }
type MsgWithAtts = { attachments?: unknown[]; file?: { _id?: string; name?: string; type?: string } };
/** 메시지 1건의 이미지 첨부 RC 경로 — 원본(title_link) 우선, 구형 file 폴백. 서버 imgTx.messageImagePaths 와 같은 규칙(경로 fileId 로 대조). */
export function messageImagePaths(m: MsgWithAtts): string[] {
	const out: string[] = [];
	for (const a of (m.attachments || []) as Array<{ image_url?: string; title_link?: string }>) {
		if (!a || !a.image_url) continue;
		const p = a.title_link || a.image_url;
		if (/^\/(?:file-upload|ufs)\//.test(p)) out.push(p);
	}
	if (out.length === 0 && m.file?._id && (m.file.type || '').startsWith('image/')) out.push(`/file-upload/${m.file._id}/${encodeURIComponent(m.file.name || 'image')}`);
	return out;
}
export async function translateImage(rid: string, msgId: string, path: string, targetLang: LangCode): Promise<ImageTranslateResult> {
	const blocked = isTranslateQuotaBlocked();
	if (blocked > 0) throw new QuotaError(blocked);
	const res = await fetch('/api/tpt/translate/image', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', ...txAuthHeaders() },
		body: JSON.stringify({ rid, msgId, path, targetLang }),
	});
	const data = await res.json().catch(() => ({})) as ImageTranslateResult & { code?: string; retryAfter?: number };
	if (res.status === 429 || data.code === 'QUOTA') {
		const retry = Number(data.retryAfter) || 30;
		if (data.code !== 'RATE') quotaBlockedUntil = Date.now() + retry * 1000;   // 개인 상한(RATE)은 엔진 한도가 아니라 전역 차단 안 함
		throw new QuotaError(retry);
	}
	if (res.status === 415) throw new Error(i18next.t('msgView.imageTxUnsupported', { defaultValue: '지원하지 않는 이미지 형식입니다.' }));
	if (res.status === 413) throw new Error(i18next.t('msgView.imageTxTooLarge', { defaultValue: '이미지가 너무 커서 글자를 읽을 수 없습니다.' }));
	if (!res.ok) throw new Error(i18next.t('errors.translate.failed'));
	return data;
}

/** 🖼 그림 번역 — 바이트 직접 (2026-09-27) — 대화방 첨부가 아닌 그림(드라이브·프로젝트·붙여넣은 그림 등). 화면에 띄운 이미지를 그대로 올린다.
 *  서버가 받는 형식(png·jpeg·webp·heic)이 아니거나 8MB 를 넘으면 캔버스로 PNG/JPEG 재인코딩(긴 변 2400px 상한). 응답은 translateImage 와 같다. */
const PIC_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/heic', 'image/heif']);
const PIC_MAX = 8 * 1024 * 1024;
async function reencodeForOcr(blob: Blob): Promise<Blob> {
	const bmp = await createImageBitmap(blob);
	const scale = Math.min(1, 2400 / Math.max(bmp.width, bmp.height));
	const c = document.createElement('canvas');
	c.width = Math.max(1, Math.round(bmp.width * scale)); c.height = Math.max(1, Math.round(bmp.height * scale));
	const g = c.getContext('2d');
	if (!g) throw new Error(i18next.t('msgView.imageTxUnsupported', { defaultValue: '지원하지 않는 이미지 형식입니다.' }));
	g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height);   // 투명 PNG·SVG 의 검은 배경 방지(OCR 대비)
	g.drawImage(bmp, 0, 0, c.width, c.height);
	const big = blob.size > PIC_MAX;
	return await new Promise<Blob>((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), big ? 'image/jpeg' : 'image/png', 0.9));
}
export async function translatePicture(blob: Blob, targetLang: LangCode): Promise<ImageTranslateResult> {
	const blocked = isTranslateQuotaBlocked();
	if (blocked > 0) throw new QuotaError(blocked);
	const body = PIC_TYPES.has(blob.type) && blob.size <= PIC_MAX ? blob : await reencodeForOcr(blob);
	const res = await fetch(`/api/tpt/translate/picture?lang=${encodeURIComponent(targetLang)}`, {
		method: 'POST', headers: { 'Content-Type': body.type, ...txAuthHeaders() }, body,
	});
	const data = await res.json().catch(() => ({})) as ImageTranslateResult & { code?: string; retryAfter?: number };
	if (res.status === 429 || data.code === 'QUOTA') {
		const retry = Number(data.retryAfter) || 30;
		if (data.code !== 'RATE') quotaBlockedUntil = Date.now() + retry * 1000;
		throw new QuotaError(retry);
	}
	if (res.status === 415) throw new Error(i18next.t('msgView.imageTxUnsupported', { defaultValue: '지원하지 않는 이미지 형식입니다.' }));
	if (res.status === 413) throw new Error(i18next.t('msgView.imageTxTooLarge', { defaultValue: '이미지가 너무 커서 글자를 읽을 수 없습니다.' }));
	if (!res.ok) throw new Error(i18next.t('errors.translate.failed'));
	return data;
}

export async function translate(text: string, targetLang: LangCode, sourceLang?: LangCode, cacheKey?: string, msgId?: string, protect?: string[]): Promise<TranslateResult> {
	const key = cacheKey ?? `${targetLang}|${text}`;
	const hit = cache.get(key);
	if (hit) return { translation: hit, cached: true, provider: 'cache' };
	// circuit-breaker — 한도 초과 중이면 네트워크 요청 없이 즉시 차단
	const blocked = isTranslateQuotaBlocked();
	if (blocked > 0) throw new QuotaError(blocked);
	// msgId 가 있으면 서버가 (msgId, targetLang) 키로 번역을 영속 저장 — 재접속/타 기기에서 재표시
	const res = await fetch('/api/tpt/translate', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', ...txAuthHeaders() },
		// 🔒 protect = 본문에서 매칭된 멘션 이름 목록 — 서버가 `@이름` 전체를 번역에서 제외 (2026-09-04 "@반환"→"@호안" 오번역)
		body: JSON.stringify({ text, targetLang, sourceLang, ...(msgId ? { msgId, srcHash: srcHashOf(text) } : {}), ...(protect && protect.length ? { protect } : {}) }),
	});
	const data = await res.json().catch(() => ({}));
	if (res.status === 429 || data.code === 'QUOTA') {
		const retry = Number(data.retryAfter) || 30;
		quotaBlockedUntil = Date.now() + retry * 1000;
		throw new QuotaError(retry);
	}
	// 🚦 서버 번역 게이트 거절 503 BUSY (2026-09-18) — 메시지별 120s 백오프 대신 **전역**으로 retryAfter(기본 30s) 동안 멈춘다.
	//   방 진입 배치(최신 50건)가 한 건씩 계속 두드려 재시도 폭주가 되는 것을 막는다. QuotaError 로 던져 자동번역 훅이 배치를 중단하게.
	if (res.status === 503 && data.code === 'BUSY') {
		const retry = Math.min(120, Number(data.retryAfter) || 30);
		quotaBlockedUntil = Date.now() + retry * 1000;
		throw new QuotaError(retry);
	}
	if (!res.ok) throw new Error(i18next.t('errors.translate.failed'));
	cache.set(key, data.translation);
	if (cache.size > 300) {
		const firstKey = cache.keys().next().value;
		if (firstKey !== undefined) cache.delete(firstKey);
	}
	return { translation: data.translation, cached: !!data.cached, provider: data.provider || '?' };
}

/** 🔒 보안(DRM) 문서 번역이 사내 API 로 안 될 때 — 외부 엔진으로 넘기지 않고 여기서 끝낸다 */
export class SecureTxError extends Error {
	reason: string;
	constructor(reason: string) { super('secure_translate_failed'); this.name = 'SecureTxError'; this.reason = reason; }
}
/**
 * 🔒 보안(DRM) 문서 번역 (2026-09-29 사용자 결정 "사내 API만 허용") — 미리보기에서 복호화해 띄운 DRM 문서 전용.
 *   서버가 사내 용어집 API 만 쓰고(Gemini 폴백 없음) 캐시·저장하지 않는다. 여기서도 메모리 캐시에 넣지 않는다.
 */
export async function translateSecureDoc(text: string, targetLang: LangCode): Promise<string> {
	const blocked = isTranslateQuotaBlocked();
	if (blocked > 0) throw new QuotaError(blocked);
	const res = await fetch('/api/tpt/translate', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', ...txAuthHeaders() },
		body: JSON.stringify({ text, targetLang, secure: true }),
	});
	const data = await res.json().catch(() => ({}));
	if (res.status === 503 && data.code === 'BUSY') {
		const retry = Math.min(120, Number(data.retryAfter) || 30);
		quotaBlockedUntil = Date.now() + retry * 1000;
		throw new QuotaError(retry);
	}
	if (data.code === 'SECURE') throw new SecureTxError(String(data.reason || 'engine'));
	if (!res.ok || typeof data.translation !== 'string') throw new SecureTxError('engine');
	return data.translation;
}
